import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  CompanyComplianceProfile,
  UpdateCompanyComplianceProfilePayload,
  GeneratedComplianceDocument,
  GeneratedDocumentVersion,
  DocumentContent,
  WizardCompleteResponse,
} from '@/lib/types';

const FIVE_MINUTES = 1000 * 60 * 5;

// ---------------------------------------------------------------------------
// Setup Wizard Profile
// ---------------------------------------------------------------------------

export function useSetupWizardProfile(enabled = true) {
  return useQuery({
    queryKey: queryKeys.setupWizard.profile(),
    queryFn: async (): Promise<CompanyComplianceProfile> => {
      const response = await apiClient.get<CompanyComplianceProfile>(
        '/api/v1/compliance/setup-wizard/'
      );
      return response.data;
    },
    staleTime: FIVE_MINUTES,
    enabled,
  });
}

export function useUpdateSetupWizard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: UpdateCompanyComplianceProfilePayload
    ): Promise<CompanyComplianceProfile> => {
      const response = await apiClient.patch<CompanyComplianceProfile>(
        '/api/v1/compliance/setup-wizard/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.setupWizard.all });
    },
  });
}

export function useCompleteSetupWizard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<WizardCompleteResponse> => {
      const response = await apiClient.post<WizardCompleteResponse>(
        '/api/v1/compliance/setup-wizard/complete/'
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.setupWizard.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.generatedDocuments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Generated Documents
// ---------------------------------------------------------------------------

export function useGeneratedDocuments() {
  return useQuery({
    queryKey: queryKeys.generatedDocuments.lists(),
    queryFn: async (): Promise<GeneratedComplianceDocument[]> => {
      const response = await apiClient.get<GeneratedComplianceDocument[]>(
        '/api/v1/compliance/generated-documents/'
      );
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useDocumentContent(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.generatedDocuments.content(id),
    queryFn: async (): Promise<DocumentContent> => {
      const response = await apiClient.get<DocumentContent>(
        `/api/v1/compliance/generated-documents/${id}/content/`
      );
      return response.data;
    },
    enabled: enabled && id > 0,
  });
}

// ---------------------------------------------------------------------------
// Document Editing (F09)
// ---------------------------------------------------------------------------

export function useUpdateDocumentBody() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: number;
      body: Record<string, unknown>;
    }): Promise<GeneratedComplianceDocument> => {
      const response = await apiClient.patch<GeneratedComplianceDocument>(
        `/api/v1/compliance/generated-documents/${id}/edit/`,
        { body }
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.generatedDocuments.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.generatedDocuments.lists(),
      });
    },
  });
}

export function usePublishRevision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      change_summary,
    }: {
      id: number;
      change_summary: string;
    }): Promise<GeneratedDocumentVersion> => {
      const response = await apiClient.post<GeneratedDocumentVersion>(
        `/api/v1/compliance/generated-documents/${id}/publish_revision/`,
        { change_summary }
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.generatedDocuments.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.generatedDocuments.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.generatedDocuments.versions(variables.id),
      });
    },
  });
}

export function useSaveAsNewDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      title,
    }: {
      id: number;
      title: string;
    }): Promise<GeneratedComplianceDocument> => {
      const response = await apiClient.post<GeneratedComplianceDocument>(
        `/api/v1/compliance/generated-documents/${id}/save_as/`,
        { title }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.generatedDocuments.all,
      });
    },
  });
}

export function useDocumentVersions(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.generatedDocuments.versions(id),
    queryFn: async (): Promise<GeneratedDocumentVersion[]> => {
      const response = await apiClient.get<GeneratedDocumentVersion[]>(
        `/api/v1/compliance/generated-documents/${id}/versions/`
      );
      return response.data;
    },
    enabled: enabled && id > 0,
  });
}
